/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { TFunction } from "i18next";
import ErrorAction from "../../Commands/ErrorAction";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nBasis";
import AnyUtil from "../../Utils/AnyUtil";
import ArrayUtil from "../../Utils/ArrayUtil";
import CloudTyp, { cloudTypen } from "./../../../Cloud/DomainDrivenDesign/Queries/CloudTypen";
import AppSnackbarItem, {
    Code,
    codeRanking,
    Level,
    levelRanking,
} from "./../AppSnackbarContext/AppSnackbarItem";
import { AppSnackbarAktion } from "./AppSnackbarAktion";
import AppSnackbarTemplateProps from "./AppSnackbarTemplate/AppSnackbarTemplateProps";

export default class AppSnackbarModel {
    private readonly _t: TFunction;

    public constructor(private readonly _items: AppSnackbarItem[]) {
        this._t = useTranslation().t;
    }

    public get hasItems(): boolean {
        return this._items.length > 0;
    }

    public get showBackdrop() {
        return this._items.some((i) => !!this.getAction(i));
    }

    public getTemplatesProps(): Array<AppSnackbarTemplateProps> {
        return [...this._items]
            .sort(this.compareItems)
            .map(this.itemToAppSnackbarTemplateProps)
            .filter((appSnackbarItem, index, appSnackbarItems) => appSnackbarItems.findIndex(item => AnyUtil.equals(item, appSnackbarItem)) === index);
    }

    private getAction(item: AppSnackbarItem): AppSnackbarAktion | undefined {
        if (item.action) {
            switch (item.action) {
                case ErrorAction.GoToMainPage:
                    return AppSnackbarAktion.ZurHauptseite;
                case ErrorAction.Reload:
                    return AppSnackbarAktion.NeuLaden;
                case ErrorAction.Nothing:
                default:
                    return undefined;
            }
        }

        switch (item.code) {
            case "EXEC_CONCURRENCY_CONFLICT":
                return AppSnackbarAktion.NeuLaden;
            case "EXEC_DATA_NOT_FOUND":
                return AppSnackbarAktion.ZurHauptseite;
            case "EXEC_INTERNAL_ERROR":
                return AppSnackbarAktion.ZurHauptseite;
            case "AUTH_NOT_AUTHORIZED":
                return AppSnackbarAktion.ZurHauptseite;
            case "AUTH_FEHLER":
                return AppSnackbarAktion.NeuAnmelden;
            case "EXEC_INVALID_OPERATION":
            case "EXEC_PDF_ERROR":
            default:
                return undefined;
        }
    }

    private getMessage(item: AppSnackbarItem): string {
        return this._t(this.toTranslationKey(item.code), { message: item.message });
    }

    public getHoechstesLevel(): Level | null {
        const templateProps = this.getTemplatesProps();
        if (ArrayUtil.isEmpty(templateProps)) {
            return null;
        }

        return templateProps.reduce<Level>(
            (max, curr) => levelRanking[curr.level] > levelRanking[max] ? curr.level : max,
            "Success");
    }

    private readonly itemToAppSnackbarTemplateProps = (
        item: AppSnackbarItem
    ): AppSnackbarTemplateProps => {
        const action = this.getAction(item);

        let cloudTyp: CloudTyp | undefined = undefined;
        if (item.referenceTypeName) {
            cloudTyp = cloudTypen.ermittle(item.referenceTypeName);
        }

        return {
            text: this.getMessage(item),
            level: item.level,
            typBezeichnung: cloudTyp?.ermittleBezeichnung(),
            typMatchcode: item.referenceMatchcode,
            typUrl: cloudTyp?.ermittleUrl(item.referenceId ?? ""),
            action,
        };
    };

    private compareItems = (a: AppSnackbarItem, b: AppSnackbarItem): number => {
        const codeRankingComparison = codeRanking[a.code] - codeRanking[b.code];
        const levelRankingComparison = levelRanking[a.level] - levelRanking[b.level];
        return codeRankingComparison ? codeRankingComparison : levelRankingComparison;
    }

    private toTranslationKey(code: Code): string {
        switch (code) {
            case "EXEC_DATA_NOT_FOUND":
                return translationKeys.appSnackbarModel.dataNotFound;
            case "EXEC_INTERNAL_ERROR":
                return translationKeys.appSnackbarModel.internalError;
            case "AUTH_NOT_AUTHORIZED":
                return translationKeys.appSnackbarModel.notAuthorized;
            default:
                return translationKeys.appSnackbarModel.default;
        }
    }
}
